import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { ClioConstants } from "../../models/clio-constants/clio-constants";
import { SendEmailService } from "../../services/send-email/send-email.service";
import { AuthService } from "../../services/auth/auth.service";
import { VerifyEligibilityService } from "../../services/verify-eligibility/verify-eligibility.service"
import { SendEmailRequest } from "../../models/request/send-email/SendEmailRequest";
import { Store } from "@ngrx/store";
import { saveEmail } from "../../stores/user-input/user-input.actions";
import { AppState } from "../../stores/app.state";
import { UserRouteTrackingService } from "../../services/user-route-tracking/user-route-tracking.service";

@Component({
  selector: 'app-sendemail',
  templateUrl: './sendemail.component.html',
  styleUrls: ['./sendemail.component.css'],
  standalone: false
})
export class SendemailComponent implements OnInit {

  years = [
    { value: '1', viewValue: '1 Years' },
    { value: '2', viewValue: '2 Years' },
    { value: '3', viewValue: '3 Years' },
    { value: '4', viewValue: '4 Years' },
    { value: '5', viewValue: '5 Years' },
    { value: '6', viewValue: '6 Years' },
    { value: '7', viewValue: '7 Years' },
    { value: '8', viewValue: '8 Years' },
    { value: '9', viewValue: '9 Years' },
    { value: '10', viewValue: '10 Years' },
    { value: '11', viewValue: '11 Years' },
    { value: '12', viewValue: '12 Years' },
    { value: '13', viewValue: '13 Years' },
    { value: '14', viewValue: '14 Years' },
    { value: '15', viewValue: '15 Years' },
    { value: '16', viewValue: '16 Years' },
    { value: '17', viewValue: '17 Years' },
    { value: '18', viewValue: '18 Years' },
    { value: '19', viewValue: '19 Years' },
    { value: '20', viewValue: '20 Years' },
  ];

  emptyEmailErrorMessage: string = ClioConstants.ENTER_EMAIL;
  invalidEmailErrorMessage: string = ClioConstants.ENTER_VALID_EMAIL;
  sendEmailForm!: FormGroup;
  noDocumentSelected: boolean = false;
  isProcessing = false;

  emailFormControl = new FormControl('', [Validators.required, Validators.email]);
  confirmEmailFormControl = new FormControl('', [Validators.required, Validators.email]);
  surveyEmailFormControl = new FormControl('', [Validators.email]);
  confirmSurveyEmailFormControl = new FormControl('', [Validators.email]);
  claimYearsFormControl = new FormControl(this.years[5].value, [Validators.required]);

  constructor(private fb: FormBuilder,
              private sendEmailService: SendEmailService,
              private verifyEligibilityService: VerifyEligibilityService,
              private router: Router,
              private authService: AuthService,
              private userRouteTrackingService: UserRouteTrackingService,
              private store: Store<AppState>) {
  }

  ngOnInit(): void {
    this.userRouteTrackingService.setLastAtEmailPage();
    this.verifyEligibilityService.verifyEligibility().subscribe(
      response => {
        if (!response.eligible) {
          // console.error(response)
          this.router.navigate(['error/record-not-found'], {
            queryParams: {
              code: 'SE-03'
            }
          });
        }
      },
      error => {
        // console.error(error);
        this.router.navigate(['error/record-not-found'], {
          queryParams: {
            code: 'SE-04'
          }
        });
      }
    );

    this.sendEmailForm = this.fb.group({
        driverfactor: false,
        personal: false,
        commercial: false,
        residential: false,
        claimhistory: false,
        yesrsOfClaimHistory: this.claimYearsFormControl,
        email: this.emailFormControl,
        confirmEmail: this.confirmEmailFormControl,
        surveyCheckBox: false,
        surveyEmail: this.surveyEmailFormControl,
        confirmSurveyEmail: this.confirmSurveyEmailFormControl
      },
      {
        validator: Validators.compose([emailMatchValidator(), surveyEmailMatchValidator(), documentsValidator()])
      }
    );
    this.authService.startSessionPolling();
    this.authService.startSessionExpiryWatcher();
  }

  ngOnDestroy() {
    this.authService.stopSessionExpiryWatcher();
  }

  sendEmail(form: FormGroup): void {
    if (this.sendEmailForm.hasError('noDocumentSelected')) {
      this.noDocumentSelected = true;
      this.sendEmailForm.markAllAsTouched();
      return;
    }
    if (!this.sendEmailForm.valid) {
      this.sendEmailForm.markAllAsTouched();
      return;
    }

    const sendEmailRequest: SendEmailRequest = {
      driverFactorReportRequired: form.controls['driverfactor'].value,
      personalDrivingRecordRequired: form.controls['personal'].value,
      commercialDrivingRecordRequired: form.controls['commercial'].value,
      residentialAddressHistoryRequired: form.controls['residential'].value,
      insuranceAndClaimsRequired: form.controls['claimhistory'].value,
      yearsOfClaimHistory: form.controls['yesrsOfClaimHistory'].value,
      email: form.controls['email'].value,
      surveyemail: form.controls['surveyEmail'].value,
      surveySelected: form.controls['surveyCheckBox'].value,
    }
    // console.log(sendEmailRequest);
    this.store.dispatch(saveEmail({ email: form.controls['email'].value }))
    this.isProcessing = true;
    this.sendEmailService.sendEmail(sendEmailRequest).subscribe({
      next: (response) => {
        //console.log('SendemailComponent: resposne from backend=' + JSON.stringify(response));
        if (response.clioResponseEnum === 'OK') {
          this.store.dispatch(saveEmail({ email: form.controls['email'].value }))
          this.isProcessing = false;
          this.router.navigate(['goodbye']);
        } else {
          this.isProcessing = false;
          this.authService.invalidateSession();
          this.router.navigate(['error/documents-not-available'], {
            queryParams: {
              code: 'SE-01'
            }
          });
        }

      },
      error: (error) => {
        // console.error(error);
        this.authService.invalidateSession();
        this.isProcessing = false;
        this.router.navigate(['error/documents-not-available'], {
          queryParams: {
            code: 'SE-02'
          }
        });
      },
      complete: () => {
      }
    });
  }

  getConfirmEmailErrorMessage(): string {
    if (this.confirmEmailFormControl.hasError('required')) {
      return ClioConstants.ENTER_EMAIL;
    } else if (this.confirmEmailFormControl.hasError('email')) {
      return ClioConstants.ENTER_VALID_EMAIL;
    } else if (this.sendEmailForm.hasError('emailNotMatched')) {
      this.confirmEmailFormControl.setErrors({
        emailNotMatched: true
      });
      return ClioConstants.EMAIL_DO_NOT_MATCH;
    }
    return '';
  }

  getSurveyEmailErrorMessage(): string {
    if (this.surveyEmailFormControl.hasError('email')) {
      return ClioConstants.ENTER_VALID_EMAIL;
    }
    if (this.surveyEmailFormControl.hasError('surveyEmailNotPresented')) {
      this.surveyEmailFormControl.setErrors({
        surveyEmailNotPresented: true
      });
      return ClioConstants.ENTER_EMAIL;
    }
    return '';
  }

  getSurveyConfirmEmailErrorMessage(): string {
    if (this.confirmSurveyEmailFormControl.hasError('email')) {
      return ClioConstants.ENTER_VALID_EMAIL;
    }
    if (this.confirmSurveyEmailFormControl.hasError('surveyConfirmEmailNotPresented')) {
      this.surveyEmailFormControl.setErrors({
        surveyConfirmEmailNotPresented: true
      });
      return ClioConstants.ENTER_EMAIL;
    } else if (this.sendEmailForm.hasError('surveyNotMatched')) {
      this.confirmSurveyEmailFormControl.setErrors({
        surveyNotMatched: true
      });
      return ClioConstants.EMAIL_DO_NOT_MATCH;
    }
    return '';
  }

  getNoDocumentedSelectedErroMessage(): string {
    return this.noDocumentSelected ? ClioConstants.SELECT_DOCUMENTS : "";
  }

  disableCheckboxErrorMessage(): void {
    this.noDocumentSelected = false;
  }

}

export function emailMatchValidator(): ValidatorFn {
  return (form: AbstractControl): ValidationErrors | null => {
    const email: string = form.get("email")?.value;
    const confirmEmail: string = form.get("confirmEmail")?.value;

    if (email && confirmEmail) {
      return (email === confirmEmail) ? null : { emailNotMatched: true };
    }

    return null;
  }
}

export function surveyEmailMatchValidator(): ValidatorFn {
  return (form: AbstractControl): ValidationErrors | null => {
    const optionalEmail: string = form.get("surveyEmail")?.value;
    const confirmOptionalEmail: string = form.get("confirmSurveyEmail")?.value;

    if (optionalEmail || confirmOptionalEmail) {
      if (!optionalEmail && confirmOptionalEmail) {
        return { surveyEmailNotPresented: true };
      } else if (optionalEmail && !confirmOptionalEmail) {
        return { surveyConfirmEmailNotPresented: true };
      } else {
        if (!(optionalEmail === confirmOptionalEmail)) {
          return { surveyNotMatched: true };
        }
      }
    }

    return null;
  }
}

export function documentsValidator(): ValidatorFn {
  return (form: AbstractControl): ValidationErrors | null => {
    const driverfactor: boolean = form.get("driverfactor")?.value;
    const personal: boolean = form.get("personal")?.value;
    const commercial: boolean = form.get("commercial")?.value;
    const residential: boolean = form.get("residential")?.value;
    const claimhistory: boolean = form.get("claimhistory")?.value;

    return (driverfactor || personal || commercial || residential || claimhistory) ? null : { noDocumentSelected: true };
  }
}
